import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Button, Heading, TextField, Grid, Alert,
} from '@printdeal/ui-library';

import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import TogglePasswordButton from '../../../authentication/TogglePasswordButton';
import { FeedbackValues } from './AccountForm';
import { ServiceFactory } from '../../../../services/ServiceFactory';
import { messages } from '../../translations/account';

interface IFormValues {
  currentPassword?: string;
  newPassword?: string;
}

const passwordSchema = z.object({
  currentPassword: z
    .string()
    .min(8, 'Password needs to be at least 8 characters'), // Minimum 8 characters for password
  newPassword: z
    .string()
    .min(8, 'Password needs to be at least 8 characters'), // Minimum 8 characters for password
});

const passwordErrorMessages = defineMessages({
  shortPasswordError: { id: 'account.detail.update.password.too.short', defaultMessage: 'Use at least 8 characters' },
});

const UpdatePasswordForm = () => {
  const intl = useIntl();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState<FeedbackValues | undefined>(undefined);
  const customerApi = ServiceFactory.createCustomerService();

  const reactHookForm = useForm<IFormValues>({
    defaultValues: { currentPassword: '', newPassword: '' },
    mode: 'onBlur',
    resolver: zodResolver(passwordSchema),
  });

  const {
    handleSubmit, formState: { errors, isSubmitting, isValid = true }, register, reset, clearErrors,
  } = reactHookForm;

  const mutation = useMutation({
    mutationFn: (values: IFormValues) => customerApi.updateAccount(values),
    onSuccess: () => {
      setPasswordFeedback({ kind: 'success', message: intl.formatMessage(messages.success) });
      reset();
    },
    onError: (error: any) => {
      if (error.name === 'InvalidPasswordError') {
        setPasswordFeedback({ kind: 'error', message: intl.formatMessage(messages.passworderror) });
      } else {
        setPasswordFeedback({ kind: 'error', message: intl.formatMessage(messages.error) });
      }
      reset();
    },
  });

  const updatePassword = (values: IFormValues) => {
    mutation.mutate(values); // Trigger the mutation
  };

  return (
    <FormProvider {...reactHookForm}>
      <form onSubmit={handleSubmit(updatePassword)} autoComplete="off" noValidate className="data-sentry-block pd-max-w-2xl">
        <Heading as="h2" className="pd-mb-6">
          <FormattedMessage id="account.details.password.update" defaultMessage="Update your password" />
        </Heading>
        {passwordFeedback && (
          <Alert
            variant={passwordFeedback.kind}
            isDismissible
            dismissLabel={intl.formatMessage({
              id: 'general.alert.dismiss',
              defaultMessage: 'Dismiss',
            })}
            className="pd-my-6"
          >
            {passwordFeedback.message}
          </Alert>
        )}
        <Grid as="fieldset" className="pd-mb-6 pd-gap-6 sm:pd-grid-cols-2">
          <Grid.Item>
            <TextField
              id="currentPassword"
              required
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              type={showCurrentPassword ? 'text' : 'password'}
              label={intl.formatMessage({
                id: 'account.details.label.currentPassword',
                defaultMessage: 'Current Password',
              })}
              validation={errors?.currentPassword ? 'error' : undefined}
              validationMessage={
                errors?.currentPassword
                && intl.formatMessage(passwordErrorMessages.shortPasswordError)
              }
              iconAfter={(
                <TogglePasswordButton
                  setShowPassword={setShowCurrentPassword}
                  showPassword={showCurrentPassword}
                  buttonLocation="currentPassword"
                />
              )}
              {...register('currentPassword', {
                onChange: () => clearErrors('currentPassword'), // Clear error on change
              })}
            />
          </Grid.Item>
          <Grid.Item>
            <TextField
              id="newPassword"
              autoComplete="new-password"
              required
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              type={showNewPassword ? 'text' : 'password'}
              label={intl.formatMessage({
                id: 'account.details.label.newPassword',
                defaultMessage: 'New Password',
              })}
              validation={errors?.newPassword ? 'error' : undefined}
              validationMessage={errors?.newPassword && intl.formatMessage(passwordErrorMessages.shortPasswordError)}
              iconAfter={(
                <TogglePasswordButton
                  setShowPassword={setShowNewPassword}
                  showPassword={showNewPassword}
                  buttonLocation="newPassword"
                />
              )}
              {...register('newPassword', {
                onChange: () => clearErrors('newPassword'), // Clear error on change
              })}
            />
          </Grid.Item>
        </Grid>
        <div className="pd-flex pd-justify-end">
          <Button
            type="submit"
            variant="secondary"
            isLoading={isSubmitting || mutation.isPending}
            disabled={!isValid || isSubmitting || mutation.isPending}
          >
            <FormattedMessage id="account.details.password.submit.button" defaultMessage="Update password" />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default UpdatePasswordForm;
